import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Users, Shield, Ban, UserCheck, Mail, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { headers: getAuthHeaders() });
      setUsers(res.data.users);
      setCounts(res.data.counts);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/admin/login'); }
      else { toast.error('Failed to load users'); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const updateRole = async (email, role) => {
    try {
      await axios.patch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/role`,
        { role }, { headers: getAuthHeaders() });
      toast.success(`Role updated to ${role}`);
      fetchUsers();
    } catch { toast.error('Failed to update role'); }
  };

  const updateStatus = async (email, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/users/${encodeURIComponent(email)}/status`,
        { status }, { headers: getAuthHeaders() });
      toast.success(`User ${status === 'banned' ? 'banned' : 'activated'}`);
      fetchUsers();
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="admin-users-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to Admin
        </Link>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-3 mb-6">
          <Users className="h-6 w-6 text-primary" /> User Management
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: counts.total || 0, color: 'text-foreground' },
            { label: 'Active', value: counts.active || 0, color: 'text-emerald-400' },
            { label: 'Admins', value: counts.admins || 0, color: 'text-primary' },
            { label: 'Banned', value: counts.banned || 0, color: 'text-red-400' },
          ].map(s => (
            <Card key={s.label} className="bg-card border-border">
              <CardContent className="py-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* User List */}
        {loading ? (
          <p className="text-center text-muted-foreground py-12">Loading users...</p>
        ) : users.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center text-muted-foreground">No registered users yet.</CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {users.map(user => (
              <Card key={user.email} className="bg-card border-border" data-testid={`user-card-${user.email}`}>
                <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground truncate">
                        {user.full_name || user.name || user.email}
                      </span>
                      {user.role === 'admin' && (
                        <Badge className="bg-primary/20 text-primary text-xs"><Shield className="h-3 w-3 mr-1" />Admin</Badge>
                      )}
                      {user.status === 'banned' && (
                        <Badge variant="destructive" className="text-xs"><Ban className="h-3 w-3 mr-1" />Banned</Badge>
                      )}
                      {user.provider === 'google' && (
                        <Badge variant="outline" className="text-xs border-border">Google</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{user.email}</span>
                      {user.created_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {user.role !== 'admin' ? (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => updateRole(user.email, 'admin')} data-testid={`make-admin-${user.email}`}>
                        <Shield className="h-3 w-3 mr-1" /> Make Admin
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => updateRole(user.email, 'user')} data-testid={`remove-admin-${user.email}`}>
                        <UserCheck className="h-3 w-3 mr-1" /> Remove Admin
                      </Button>
                    )}
                    {user.status !== 'banned' ? (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/40 text-red-400 hover:bg-red-500/10"
                        onClick={() => updateStatus(user.email, 'banned')} data-testid={`ban-${user.email}`}>
                        <Ban className="h-3 w-3 mr-1" /> Ban
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => updateStatus(user.email, 'active')} data-testid={`activate-${user.email}`}>
                        <UserCheck className="h-3 w-3 mr-1" /> Activate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
